import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useProfile } from "../../context/ProfileProvider/useProfile";
import { Organization, OrganizationUserServiceVO } from "../../model/OrganizationModel";
import { AlertColor, Box, Button, CircularProgress, Divider, FormControlLabel, Grid, MenuItem, Pagination, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { IPaginationItems } from "../../services/Pagination";
import { WorkSpace, WorkspaceUserPermission } from "../../model/WorkSpaceModel";
import { IFavoriteModel } from "../../model/ItemModel";
import { getAllFavorites } from "../../services/favoriteServices";
import { CreateOrganization, DeleteOrganization, Edit, GetD4SignSafeBox, GetOneOrganization, GetOrganization, GetOrganizationAdminUsers, GetOrganizationTrim, GetOrganizationTrimName, GetOrganizationWorkspaceAdmTrimName, SetD4Sign, SetUserService, UnsetD4Sign, UnsetUserService } from "../../services/OrganizationServices";
import { useTranslation } from "react-i18next";
import { AdminConsoleWrapper } from "../../pages/AdminConsole/components/AdminConsoleWrapper/AdminConsoleWrapper";
import { KeydriveOrganizationAvatar } from "../KeydriveComponents/Avatar/KeydriveOrganizationAvatar";
import { KeyDriveh4Header } from "../KeydriveComponents/Fonts/Headers/KeyDriveh4Header";
import { KeyDriveBodyMin } from "../KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { Add, Apartment, Close, Delete, Info, Save, SaveOutlined, Settings, Workspaces } from "@mui/icons-material";
import { KeyDriveTab2 } from "../KeydriveComponents/Tabs/KeydriveTab2";
import { KeyDriveTabPanel } from "../KeydriveComponents/Tabs/components/KeyDriveTabPanel";
import { EmptyContainer } from "../KeydriveComponents/EmptyContainer/EmptyContainer";
import { NoWorkspacesOrganizationIcon } from "../../assets/svg/NoWorkspacesOrganizationIcon";
import LoaderComp from "../Loader/LoaderComp";
import { WorkspaceCard } from "../../pages/AdminConsole/Workspaces/components/WorkspaceCard/WorkspaceCard";
import { KeyDrivePrimaryButton } from "../KeydriveComponents/Buttons/KeyDrivePrimaryButton";
import { KeydriveTextfield } from "../KeydriveComponents/Textfield/KeydriveTextfield";
import { KeyDriveBodyMax } from "../KeydriveComponents/Fonts/Body/KeyDriveBodyMax";
import { RuleCard } from "../../pages/AdminConsole/Workspaces/components/NewWorkspace/components/RuleCard";
import ApartmentSelect from "../../assets/icons/ApartmentSelect";
import KeydriveSnackbar from "../KeydriveComponents/Snackbar/KeydriveSnackbar";
import { KeyDriveDialog } from "../KeydriveComponents/Dialog/KeyDriveDialog";
import styles from "./AdminOrganization.module.css"
import { useNavigate, useParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { OrganizationComponentCard } from "./OrganizationComponentCard";
import { IOptionsSelect } from "../../pages/Organization/OrganizationConfig";
import { OrganizationHelpers } from "../../helpers/OrganizationHelpers";

interface IOrganizationInfo {
    id: string;
    name: string;
    internalName: string;
    driveAccess: boolean;
    description: string;
}

interface IOrganizationComponent {
    isAdminPage: boolean;
}

function useVisibility(
  cb: (isVisible: boolean) => void,
  deps: React.DependencyList
): (node: any) => void {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  return useCallback((node) => {
    if (intersectionObserver.current) {
      intersectionObserver.current.disconnect();
    }

    intersectionObserver.current = new IntersectionObserver(([entry]) => {
      cb(entry.isIntersecting);
    });

    if (node) intersectionObserver.current.observe(node);
  }, deps);
}

export const OrganizationComponent = ({
    isAdminPage
}: IOrganizationComponent) => {
        const [loading, setLoading] = useState<boolean>(false);
        const [loadingOrgInfo, setLoadingOrgInfo] = useState<boolean>(false);
        const [organizations, setOrganizations] = useState<Organization[]>([]);
        const { nameOrg } = useParams();
        const profile = useProfile();
        const LIMIT = 15;
        const [skip, setSkip] = useState<number>(0);
        const [name, setName] = useState<string>("");
        const [organizationSelected, setOrganizationSelected] = useState<Organization | undefined>(undefined);
        const [message, setMessage] = useState<string | undefined>(undefined);
        const [alertColor, setAlertColor] = useState<AlertColor>("success");
        const [value, setValue] = useState<number>(0);
        const [workspaces, setWorkspaces] = useState<IPaginationItems<WorkSpace[]>>({ items: [], totalItems: 0 });
        const [currentPage, setCurrentPage] = useState<number>(1);
        const [refresh, setRefresh] = useState<boolean>(false);
        const [favorites, setAllFavorites] = useState<IFavoriteModel[]>([]);
        const [organizationInfo, setOrganizationInfo] = useState<IOrganizationInfo>({
            driveAccess: false,
            id: "",
            internalName: "",
            name: "",
            description: ""
        });
        const [keySelectAdminUsers, setKeySelectAdminUsers] = useState<string | null>(null);
        const [isDeleteOrgDialogOpen, setIsDeleteOrgDialogOpen] = useState<boolean>(false);
        const [isCreateOrgDialogOpen, setIsCreateOrgDialogOpen] = useState<boolean>(false);
        const [newOrganizationName, setNewOrganizationName] = useState<string>("");
        const [newOrganizationDescription, setNewOrganizationDescription] = useState<string>("");
        const navigate = useNavigate();
        const [organizationMax, setOrganizationMax] = useState<boolean>(true);
        const [debouncedName, setDebouncedName] = useState<string>("");
        const [isCreatingOrganization, setIsCreatingOrganization] = useState<boolean>(false);
        const [creatingOrganizationerrorMessage, setCreatingOrganizationErrorMessage] = useState<string |  undefined>(undefined);
        const [conclusionDialog, setConclusionDialog] = useState<boolean>(false);
        const [originalServiceUser, setOriginalServiceUser] = useState<string>("");
        const [optionsAdminUsers, setOptionsAdminUsers] = useState<IOptionsSelect[]>([]);
        const [options, setOptions] = useState<IOptionsSelect[]>([]);
        const [keySelect, setKeySelect] = useState<string | null>(null);
        const [originalD4SignSafe, setOriginalD4SignSafe] = useState<string | null>(null);
        const [safeBox, setSafeBox] = useState<string | null>(null);

        const resetOrganizationInfo = (): void => {
            setNewOrganizationDescription("");
            setNewOrganizationName("");
            setCreatingOrganizationErrorMessage("");
        }

        function unsaveUserService() {
            if (organizationSelected != undefined && organizationSelected.id != undefined) {
                unsetServiceUser(organizationSelected.id);
            }
        }

        function unsetServiceUser(thisIdOrg:string) {
            UnsetUserService(thisIdOrg)
            .then(()=>{
                setMessage("Usuário de serviço desatribuído com sucesso");
                setAlertColor("success");
                if (organizationSelected != undefined && organizationSelected.id != undefined) {
                    setOrganizationSelected({...organizationSelected, serviceUserId: undefined});
                }
            })
            .catch((error)=> {
                if( error.response && error.response.data && typeof error.response.data  === 'string')
                {
                    setMessage(error.response.data);
                    setAlertColor("error");
                }
                else
                {
                    setMessage("Usuário de serviço desatribuído com sucesso");
                    setAlertColor("success");
                }
            });
        }

        function unSaveD4Sign() {
            if (organizationSelected != undefined && organizationSelected.id != undefined) {
                unsetInternal(organizationSelected.id)
            }
        }

          function d4Sign()
          {
            GetD4SignSafeBox()
            .then((result) =>{
              let optionInter:IOptionsSelect[] = [];
              result.map((d4SignSafeBox) =>
              {
                optionInter.push({
                  key:d4SignSafeBox.uuidSafe,
                  text:d4SignSafeBox.nameSafe
                });
              });
              setOptions(optionInter);
            })
            .catch((error)=>{
              if( error.response && error.response.data && typeof error.response.data  === 'string')
              {
                setMessage(error.response.data);
                setAlertColor("error");
              }
              else
              {
                setMessage(t("OrganizationConfig.ErrorGettingSafe"));
                setAlertColor("error");
              }
        
              setKeySelect(null);
              setOptions([]);
            })
          }
        

          function unsetInternal(thisIdOrg:string)
          {
            UnsetD4Sign(thisIdOrg)
            .then(()=>{
              setOriginalD4SignSafe(null);
              setSafeBox(null);
              setKeySelect(null);
        
              setMessage("Cofre desatribuído com sucesso");
              setAlertColor("success");
              
              if (organizationSelected) {
                setOrganizationSelected({...organizationSelected, d4SignSafeBox: undefined});
              }
            })
            .catch((error)=> {
              if( error.response && error.response.data && typeof error.response.data  === 'string'){
                setMessage(error.response.data);
                setAlertColor("error");      
              }
              else
              {
                setMessage(t("OrganizationConfig.SomethingWentWrong"));
                setAlertColor("error");
              }
            });
          }
        

        function EditUserService() {
            if (organizationSelected != null && organizationSelected.id) {
                let itemModelo: OrganizationUserServiceVO = {
                organizationId: organizationSelected?.id,
                serviceUserId: keySelectAdminUsers!
                };
                
                SetUserService(itemModelo)
                .then(() => {
                    setOriginalServiceUser(keySelectAdminUsers!);
                    setMessage(t("OrganizationConfig.SuccessfullyEdited"));
                    setAlertColor("success");
        
                    setOrganizationSelected({...organizationSelected, serviceUserId: keySelectAdminUsers!});
                })
                .catch((error)=> {
                    if( error.response && error.response.data && typeof error.response.data  === 'string'){
                        setMessage(error.response.data);
                        setAlertColor("error");
                    }
                    else
                    {
                        setMessage(t("OrganizationConfig.SomethingWentWrong"));
                        setAlertColor("error");
                    }
                });
            }
        }

        function getAdminUsers()
        {
            if(organizationSelected){
                GetOrganizationAdminUsers(organizationSelected?.internalName!)
                .then((result) =>{
                let optionInter: IOptionsSelect[] = [];
                result.map((adminUsers) =>
                {
                    optionInter.push({
                    key:adminUsers.id,
                    text:adminUsers.name
                    });
                });
                    setOptionsAdminUsers(optionInter);
                })
                .catch((error)=>{
                    if( error.response && error.response.data && typeof error.response.data  === 'string')
                    {
                        setMessage(error.response.data);
                        setAlertColor("error");
                    }
                    else
                    {
                        setMessage(t("ErrorReceivingAdminUsers.message"));
                        setAlertColor("error");
                    }
                    setOptionsAdminUsers([]);
                })
            }
        }

        const changeOrgInfo = (field: string, value: any) => {
            setOrganizationInfo(prev => ({ ...prev, [field]: value }));
        };
    
        const fetchAllFavorites = useCallback(async (nameOrg: string, userId: string) => {
            try {
                setLoading(true);
                if (nameOrg) {
                    const response = await getAllFavorites(nameOrg, userId!);
                    setAllFavorites(response.favoriteUserVO);
                }
            } catch (err: any) {
                setMessage(err.response.data);
                setAlertColor("error");
            } finally {
                setRefresh(false);
                setLoading(false);
            }
        }, []);
    
        const fetchAllWorkspaces = useCallback(async (page: number, nameOrg: string) => {
            try {
                setLoading(true);
                const skip = (page - 1) * LIMIT;
                const response = await GetOrganizationWorkspaceAdmTrimName(
                    nameOrg!,
                    skip,
                    8,
                    true,
                    ""
                );
                setWorkspaces(response);
            } catch (err: any) {
                setMessage(err.response.data);
                setAlertColor("error");
            } finally {
                setLoading(false);
            }
        }, []);

        const fetchUserSearchingOrganizations = async () => {
            setLoading(true);
            setOrganizations([]);
            try {
                const response = await GetOrganizationTrimName(0, LIMIT, false, name);

                if (response.length < LIMIT) {
                    setOrganizationMax(false);
                } 
                setOrganizations(response);
            } catch (err: any) {
                setMessage(err.response.data);
                setAlertColor("error");
            } finally {
                setLoading(false);
            }   
        }
    
        const fetchUserOrganizations = async () => {
            setLoading(true);
            try {
                const response = await GetOrganizationTrim(0, LIMIT, false);
                setOrganizations(response);

                if (response.length < LIMIT) {
                    setOrganizationMax(false);
                }

            } catch (err: any) {
                setMessage(err.response.data);
                setAlertColor("error");
            } finally {
                setLoading(false);
            }
        };
    
        const fetchOrganization = useCallback(async (internalName: string) => {
            setLoadingOrgInfo(true);
            try {
                const response = await GetOneOrganization(internalName);
                setOrganizationSelected(response!);
                setKeySelectAdminUsers(response?.serviceUserId ?? null)
            } catch (err: any) {
                setMessage(err.response.data);
                setAlertColor("error");
            } finally {
                setLoadingOrgInfo(false);
            }
        }, []);

          function saveD4Sign() {
            if (organizationSelected != undefined && organizationSelected.id != undefined) {
              if (keySelect != null) {
                SetD4Sign({
                  organizationId:organizationSelected?.id,
                  d4SignSafeBox:keySelect
                })
                .then(()=>{
                  setOriginalD4SignSafe(keySelect);
                  setSafeBox(keySelect);
                  
                  setOrganizationSelected({...organizationSelected, d4SignSafeBox: keySelect});
                })
                .catch((error)=> {
                  if( error.response && error.response.data && typeof error.response.data  === 'string'){
                    setMessage(error.response.data);
                    setAlertColor("error");
                  }
                  else
                  {
                    setMessage(t("OrganizationConfig.SomethingWentWrong"));
                    setAlertColor("error");
                  }
                });
              }
              else
              {
                unsetInternal(organizationSelected.id)
              }
            }
          }
    
        useEffect(() => {
            let isAdminConsoleUrl = window.location.pathname.toLowerCase().includes("adminconsole");
            if (isAdminConsoleUrl) {
                let internalName = window.location.pathname.split("/")[1];
                if (internalName != undefined) {
                    fetchOrganization(internalName);
                }
            }
        }, [window.location]);

        useEffect(() => {
            if (debouncedName != "") {
                fetchUserSearchingOrganizations();
            } else {
                fetchUserOrganizations()
            }
        }, [debouncedName]);


        useEffect(() => { 
            if (organizationSelected) {
                fetchAllWorkspaces(1, organizationSelected.internalName!);
                fetchAllFavorites(organizationSelected.internalName!, profile.id!);
                changeOrgInfo("id", organizationSelected.id!);
                changeOrgInfo("name", organizationSelected.name!);
                changeOrgInfo("description", organizationSelected.description ?? "");
                changeOrgInfo("driveAccess", organizationSelected.allowDrive!);
                getAdminUsers();
                d4Sign();
                setValue(0);
            }
        }, [fetchAllWorkspaces, fetchAllFavorites, organizationSelected, refresh]);

        const debouncedSetName = useMemo(
            () => debounce((value: string) => setDebouncedName(value), 600),
            []
        );

        useEffect(() => {
            return () => {
                debouncedSetName.cancel();
            };
        }, [debouncedSetName]);
    
        const { t } = useTranslation();
    
        const totalPages = Math.ceil(workspaces.totalItems / LIMIT);
    
        const countUsers = (permission?: WorkspaceUserPermission, adminUsers?: string[]): number => {
            const allUsers = [
                ...(permission?.adminUsers ?? []),
                ...(permission?.organizeUsers ?? []),
                ...(permission?.commonUsers ?? []),
                ...(permission?.readUsers ?? []),
                ...(adminUsers ?? [])
            ];
    
            return new Set(allUsers).size;
        };

        function getOrganization(organizationId: string) {
            GetOrganization()
            .then((organizations) => {
                if (organizations != null) {
                for(let organization of organizations){
                    if(organization.id === organizationId){
                        window.location.replace(`/${organization.internalName}`);
                        return;
                    }
                }
                } else {
                    setMessage("Não encontrada a organização");
                    setAlertColor("error");
                }
            })
            .catch(() => {
                setMessage("Não encontrada a organização");
                setAlertColor("error");
            });
        }

        const renderD4sign = (): React.ReactNode => {
            if (!profile?.adminSystem) return;

            return (
                <>                
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            fontSize: '16px', 
                            fontWeight: 600, 
                            color: '#000'
                        }}
                    >
                        {t("OrganizationConfig.ElectronicSignature")}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, justifyContent: "end" }}>
                    <Button
                        variant="contained"
                        onClick={saveD4Sign}
                        disabled={keySelect === originalD4SignSafe}
                        sx={{
                        textTransform: 'none',
                        bgcolor: '#22638D',
                        fontSize: '13px',
                        fontWeight: 400,
                        px: 2.5,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#1976D2',
                            boxShadow: 'none'
                        },
                        '&:disabled': {
                            bgcolor: '#E0E0E0',
                            color: '#9E9E9E'
                        }
                        }}
                        startIcon={<SaveOutlined />}
                    >
                        {t("OrganizationConfig.Save")}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={unSaveD4Sign}
                        disabled={originalD4SignSafe == undefined || originalD4SignSafe == null}
                        sx={{
                        textTransform: 'none',
                        borderColor: '#22638D',
                        color: '#22638D',
                        fontSize: '13px',
                        fontWeight: 400,
                        px: 2.5,
                        '&:hover': {
                            borderColor: '#9E9E9E',
                            bgcolor: 'transparent'
                        },
                        '&:disabled': {
                            borderColor: '#E0E0E0',
                            color: '#9E9E9E'
                        }
                        }}
                        startIcon={<Close />}
                    >
                        Deletar atribuição de cofre
                    </Button>
                    </Box>
                    </Box>
                    {options?.length <= 0 ? (
                        <LoaderComp />
                    ) : (
                        <Box sx={{ 
                            border: '1px solid #E0E0E0', 
                            borderRadius: '6px',
                            p: 2,
                            bgcolor: '#FFF'
                        }}>
                        <RadioGroup
                            value={keySelect || ""}
                            onChange={(e) => setKeySelect(e.target.value)}
                        >
                            {options.map((d4sign) => (
                            <FormControlLabel
                                key={d4sign.key}
                                value={d4sign.key}
                                control={
                                <Radio 
                                    size="small"
                                    sx={{
                                    color: '#9E9E9E',
                                    '&.Mui-checked': {
                                        color: '#2196F3',
                                    },
                                    }}
                                />
                                }
                                label={
                                <Typography variant="body2" sx={{ fontSize: '14px', color: '#424242' }}>
                                    {d4sign.text}
                                </Typography>
                                }
                                sx={{ mb: 0.5 }}
                            />
                            ))}
                        </RadioGroup>
                        </Box>
                    )}
                </>
            )
        }

        const workspaceRender = () => {
            return (
                <>
                    {workspaces.items?.length <= 0 && !loading ? (
                        <div style={{ height: "calc(100vh - 370px)" }}>
                            <EmptyContainer
                                content={t('AdminConsole.NoFoundWorkspaceSubtittle')}
                                title={t('AdminConsole.NotFoundWorkspace')}
                                icon={<NoWorkspacesOrganizationIcon />}
                            />
                        </div>
                    ) : (
                        <>
                            {loading ? (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    width="100%"
                                >
                                    <LoaderComp />
                                </Box>
                            ) : (
                                <div style={{
                                    height: "calc(100vh - 370px)",
                                    overflowY: "auto",
                                    background: '#FFFFFF 0% 0% no-repeat padding-box',
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "19px"
                                }}>
                                    {workspaces.items?.map(({
                                        name,
                                        adminUsers,
                                        permission,
                                        id,
                                        description
                                    }, index, list) => (
                                        <WorkspaceCard
                                            key={id}
                                            name={name!}
                                            description={description != undefined ? description : t("AdminConsole.NoDescription")}
                                            users={countUsers(permission, adminUsers)}
                                            index={index}
                                            workspacesLength={list.length}
                                            id={id!}
                                            setLoading={setLoading}
                                            setRefresh={setRefresh}
                                            favorites={favorites}
                                            itemId={favorites.filter(x => x.favoriteName == name)[0]?.id!}
                                        />
                                    ))}
                                    {totalPages > 1 && (
                                        <Box width="100%" display="flex" justifyContent="center" marginTop="20px">
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={(event, page) => {
                                                    setCurrentPage(page);
                                                }}
                                                sx={{
                                                    '& .Mui-selected': {
                                                        backgroundColor: '#E8EFF3',
                                                        color: '#22638D',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </>
            )
        }

        const tabhandler = (internalName: string) => {
            if (isAdminPage) {
                return (
                    <KeyDriveTab2
                        label1={"Workspaces"}
                        label2={t("Sidebar.Preferences")}
                        icon1={<Workspaces />}
                        icon2={<Settings />}
                        onChange={(ev, i) => { setValue(i) }}
                    >
                        <KeyDriveTabPanel value={value} index={0}>
                            {workspaces.items?.length <= 0 && !loading ? (
                                <div style={{ height: "calc(100vh - 370px)" }}>
                                    <EmptyContainer
                                        content={t('AdminConsole.NoFoundWorkspaceSubtittle')}
                                        title={t('AdminConsole.NotFoundWorkspace')}
                                        icon={<NoWorkspacesOrganizationIcon />}
                                    />
                                </div>
                            ) : (
                                <>
                                    {loading ? (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            width="100%"
                                        >
                                            <LoaderComp />
                                        </Box>
                                    ) : (
                                        <div style={{
                                            height: "calc(100vh - 600px)",
                                            overflowY: "auto",
                                            background: '#FFFFFF 0% 0% no-repeat padding-box',
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "10px"
                                        }}>
                                            {workspaces.items?.map(({
                                                name,
                                                adminUsers,
                                                permission,
                                                id,
                                                description
                                            }, index, list) => (
                                                <WorkspaceCard
                                                    key={id}
                                                    name={name!}
                                                    description={description != undefined ? description : t("AdminConsole.NoDescription")}
                                                    users={countUsers(permission, adminUsers)}
                                                    index={index}
                                                    workspacesLength={list.length}
                                                    id={id!}
                                                    setLoading={setLoading}
                                                    setRefresh={setRefresh}
                                                    favorites={favorites}
                                                    itemId={favorites.filter(x => x.favoriteName == name)[0]?.id!}
                                                />
                                            ))}
                                            { workspaces.totalItems > 8 && (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    width="100%"
                                                >
                                                    <KeyDrivePrimaryButton 
                                                        TextComponent={KeyDriveBodyMin}
                                                        TextComponentProps={{
                                                            text: t("AdminConsole.SeeAllWorkspaces"),
                                                            color: "#FFFFFF",
                                                            needBold: false,
                                                            needEllipses: false
                                                        }}
                                                        onClick={e => {
                                                            navigate(`/${internalName}/adminConsole/workspaces`);
                                                        }}
                                                    />
                                                </Box>
                                            ) }
                                        </div>
                                    )}
                                </>
                            )}
                        </KeyDriveTabPanel>
                        <KeyDriveTabPanel value={value} index={1}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom="14px">
                                <KeyDriveh4Header 
                                    text={t("AdminConsole.OrganizationInformation")}
                                    color="#2D2D2D"
                                    needBold={false}
                                    needEllipses={false}
                                />
                                <Box display="flex" gap={2}>
                                    <KeyDrivePrimaryButton 
                                        TextComponent={KeyDriveBodyMin}
                                        TextComponentProps={{
                                            color: "#FFFFFF",
                                            needBold: false,
                                            needEllipses: false,
                                            text: t("DetailDocumentPage.Save")
                                        }}
                                        onClick={() => {
                                            setConclusionDialog(true);
                                        }}
                                        startIcon={<Save sx={{ color: "#FFFFFF" }} />}
                                    />
                                </Box>
                            </Box>
                            <Box marginBottom="20px">
                                <KeydriveTextfield 
                                    label={t("AdminConsole.OrganizationNameLabel")}
                                    value={organizationInfo.name}
                                    onChange={(e) => changeOrgInfo("name", e.target.value)}
                                    needFullWidth
                                />
                            </Box>
                            <KeydriveTextfield 
                                label={t("AdminConsole.OrganizationDescriptionLabel")}
                                value={organizationInfo.description}
                                onChange={(e) => changeOrgInfo("description", e.target.value)}
                                needFullWidth
                            />
                            <Box mt="20px">
                                <KeyDriveBodyMax
                                    color="#2D2D2D"
                                    needBold
                                    needEllipses={false}
                                    text={t("AdminConsole.Preferences.Storage")}
                                    sxStyles={{ mt: "8px" }}
                                />
                            </Box>
                            <RuleCard 
                                text={t("AdminConsole.Preferences.AllowDrive")}
                                checked={organizationInfo.driveAccess}
                                onChange={e => changeOrgInfo("driveAccess", e.target.checked)}
                                width="100%"
                            />
                            <Box sx={{ mb: 4, mt: 4 }}>
                                <Typography variant="body2" sx={{ mb: 0.5, fontSize: '13px', color: '#424242' }}>
                                    {t("OrganizationConfig.ServiceUser")}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                    fullWidth
                                    select
                                    value={keySelectAdminUsers || ""}
                                    onChange={(e) => setKeySelectAdminUsers(e.target.value)}
                                    size="small"
                                    SelectProps={{
                                    displayEmpty: true,
                                    }}
                                    sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        '& fieldset': {
                                        borderColor: '#D0D0D0',
                                        }
                                    }
                                    }}
                                    variant="standard"
                                >
                                    {optionsAdminUsers.map((user) => (
                                        <MenuItem key={user.key} value={user.key}>
                                            {user.text}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Button
                                    variant="contained"
                                    onClick={EditUserService}
                                    disabled={keySelectAdminUsers === originalServiceUser}
                                    sx={{
                                        textTransform: 'none',
                                        bgcolor: '#22638D',
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        px: 2.5,
                                        minWidth: 'auto',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            bgcolor: '#1976D2',
                                            boxShadow: 'none'
                                        },
                                        '&:disabled': {
                                            bgcolor: '#E0E0E0',
                                            color: '#9E9E9E'
                                        }
                                    }}
                                    startIcon={<SaveOutlined />}
                                >
                                    {t("OrganizationConfig.Save")}
                                </Button>
                                </Box>
                            </Box>
                            { renderD4sign() }
                            <Box marginTop="20px" display="flex" flexDirection="column" gap={2}>
                                <KeyDriveBodyMax 
                                    text={t("AdminConsole.ExcludeOrg")}
                                    color="#2D2D2D"
                                    needBold={true}
                                    needEllipses={false}
                                />
                                <KeyDriveBodyMin 
                                    text={t("AdminConsole.ExcludeOrgContent")}
                                    needBold={false}
                                    needEllipses={false}
                                    color="#6C6C6C"
                                />
                                <KeyDrivePrimaryButton 
                                    backgroundColor="#BF1111"
                                    onClick={() => setIsDeleteOrgDialogOpen(true)}
                                    TextComponent={KeyDriveBodyMin}
                                    TextComponentProps={{
                                        color: "#FFFFFF",
                                        needBold: false,
                                        needEllipses: false,
                                        text: "Excluir"
                                    }}
                                    width="76px"
                                    startIcon={<Delete />}
                                    disabled={organizationSelected?.internalName == nameOrg}
                                />
                            </Box>
                        </KeyDriveTabPanel>
                    </KeyDriveTab2>
                )
            }

            return (
                <>
                    {  workspaceRender() }
                </>
            )
        }

        function handleOrganizationDescription(): string {
            let nA = "N/A";
            if (organizationSelected === undefined) return nA;

            if (organizationSelected?.description === undefined) return nA;
            
            if (organizationSelected?.description === "" || organizationSelected?.description === " ") return nA;

            return organizationSelected?.description;
        }

        const lastOrganization = useVisibility(
            (visible) => {
                if (visible) {
                    if (name == "") {
                        GetOrganizationTrim(skip, LIMIT, false)
                        .then(response => {
                            if (response.length < LIMIT) {
                                setOrganizationMax(false);
                            }
                            setSkip(prev => prev + response.length);
                            setOrganizations(previous => [...previous, ...response]);
                        })
                    } else {
                        GetOrganizationTrimName(skip, LIMIT, false, name)
                        .then(response => {
                            if (response.length < LIMIT) {
                                setOrganizationMax(false);
                            }
                            setSkip(prev => prev + response.length);
                            setOrganizations(previous => [...previous, ...response]);
                        })
                    }
                }
            }, [skip, organizations]);

    return (
        <AdminConsoleWrapper>
            <Grid container spacing={4}>
                <Grid item xs={4}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap="12px"
                        marginBottom="12px"
                    >
                        <KeydriveOrganizationAvatar size="small" />
                        <KeyDriveh4Header 
                            text={t("AdminConsole.Organization")}
                            color="#2D2D2D"
                            needBold
                            needEllipses={false}
                        />
                    </Box>
                    <Box marginBottom="20px">
                        <KeyDriveBodyMin 
                            text={t("AdminConsole.OrganizationPageContent")}
                            color="#575757"
                            needBold={false}
                            needEllipses={false}
                        />
                    </Box>
                    
                    <Divider />

                    <Box marginTop="20px" 
                        display="flex" 
                        gap={2}
                        >
                        <TextField 
                            variant="outlined"
                            fullWidth
                            placeholder={t("RecicleBin.search")}
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                debouncedSetName(e.target.value);
                            }}
                            sx={{
                                height: "56px",
                            }}
                            inputProps={{
                                border: "1px solid #E9EFF4"
                            }}
                        />
                        { isAdminPage && (
                            <Box
                                sx={{
                                    width:"56px",
                                    height:"56px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px solid #E9EFF4",
                                    cursor: "pointer"
                                }}
                                onClick={() => setIsCreateOrgDialogOpen(true)}
                            > 
                                <Add sx={{ color: "#242c31" }} />
                            </Box>
                        ) }
                    </Box>
                    <Box
                        sx={{
                            cursor: "pointer",
                            textOverflow: "hidden",
                            whiteSpace: "nowrap",
                            overflowY: "scroll",
                            maxHeight: "560px"
                        }}
                    >
                        { profile.adminSystem ? organizations?.map(org => (
                            <OrganizationComponentCard 
                                fetchOrganization={fetchOrganization}
                                org={org}
                                organizationSelected={organizationSelected!}
                                isAdminPage={isAdminPage}
                                ref={organizations[organizations.length - 1]?.id == org.id && organizationMax ? lastOrganization : null}
                            />
                        )) : (
                            <>
                                {
                                    organizations?.filter(org => OrganizationHelpers.UserInOrganization(org, profile?.id!))?.map(org => (
                                        <OrganizationComponentCard 
                                            fetchOrganization={fetchOrganization}
                                            org={org}
                                            organizationSelected={organizationSelected!}
                                            isAdminPage={isAdminPage}
                                            ref={organizations[organizations.length - 1]?.id == org.id && organizationMax ? lastOrganization : null}
                                        />
                                    ))
                                }
                            </>
                        ) }
                    </Box>
                </Grid>
                <Grid item xs={8}>
                    { organizationSelected ? (
                        <>
                            { loadingOrgInfo ? <CircularProgress variant="indeterminate" /> : (
                                <>
                                    <div className={styles.backgroundOrgDiv}>
                                        <KeydriveOrganizationAvatar size="medium"/>
                                        <Box marginLeft="12px">
                                            <KeyDriveh4Header 
                                                text={organizationSelected.name!}
                                                needBold={false}
                                                needEllipses={false}
                                                color="#FFFFFF"
                                            />
                                            <KeyDriveBodyMin 
                                                text={handleOrganizationDescription()}
                                                color="#EAEAEA"
                                                needBold={false}
                                                needEllipses={false}
                                            />
                                        </Box>
                                    </div>
                                    { tabhandler(organizationSelected.internalName!) }
                                </>
                            ) }
                        </>
                    ) : (
                        <EmptyContainer 
                            title={t("AdminConsole.NotSelectedOrganization")}
                            content={t("AdminConsole.NotSelectedOrganizationContent")}
                            icon={<ApartmentSelect />}
                        />
                    ) }
                </Grid>
            </Grid>
            
            <KeydriveSnackbar 
                open={message != undefined}
                message={message!}
                onClose={() => setMessage(undefined)}
                variant={alertColor}
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonText: t("AdminConsole.Confirm"),
                    leftButtonText: t("AdminConsole.Cancel"),
                    leftButtonMethod: () => { setIsDeleteOrgDialogOpen(false) },
                    rightButtonMethod: () => {
                        DeleteOrganization(organizationSelected?.id!)
                        .then(() => {
                            setValue(0);
                            setIsDeleteOrgDialogOpen(false);
                            setOrganizationSelected(undefined);
                            fetchUserOrganizations();
                        })
                        .catch(err => {
                            setIsDeleteOrgDialogOpen(false);
                            setMessage(err.response.data);
                            setAlertColor("error");
                        })
                    }
                }}
                keyDriveDialogContent={{
                    dialogContent: t("AdminConsole.OrgnizationDeleteText") + " " + organizationSelected?.name!,
                    dialogIcon: <Delete sx={{
                        width: "40px",
                        height: "44px",
                        color: "#22638D"
                    }} />,
                    dialogTitle: t("AdminConsole.OrganizationDeleteTitle"),
                    handleTogglDialog: () => {},
                    openDialog: isDeleteOrgDialogOpen
                }}
                dialogTitleCenter
                width="560px"
                height="300px"
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    rightButtonText: t("AdminConsole.Confirm"),
                    leftButtonText: t("AdminConsole.Cancel"),
                    leftButtonMethod: () => { setIsCreateOrgDialogOpen(false); resetOrganizationInfo(); },
                    rightButtonMethod: () => {
                        if (newOrganizationName == "" || newOrganizationName == undefined) {
                            setCreatingOrganizationErrorMessage(t("AdminConsole.OrganizationNameNotNull"));
                            return;
                        }

                        setCreatingOrganizationErrorMessage(undefined);

                        CreateOrganization({
                            name: newOrganizationName,
                            description: newOrganizationDescription
                        })
                        .then(() => {
                            setValue(0);
                            setIsCreatingOrganization(true);
                            setIsCreateOrgDialogOpen(false);
                            resetOrganizationInfo()
                            setRefresh(true);
                            setNewOrganizationDescription("");
                            setNewOrganizationName("");
                            fetchUserOrganizations();
                        })
                        .catch(err => {
                            setCreatingOrganizationErrorMessage(err.response.data);
                            setAlertColor("error");
                        })
                        .finally(() => {
                            setIsCreatingOrganization(false);
                        })
                    }
                }}
                keyDriveDialogContent={{
                    dialogContent: 
                    <>
                        { isCreatingOrganization ? (
                            <Box display="flex" justifyContent="center">
                                <LoaderComp big />
                            </Box>
                        ) : (
                            <>
                                <Box textAlign="center">
                                    <KeyDriveBodyMin 
                                        text={t("AdminConsole.OrganizationCreateText")}
                                        color="#818181"
                                        needBold={false}
                                        needEllipses={false}
                                    />
                                </Box>
                                <Box display="flex" flexDirection="column" gap="10px">
                                    <KeydriveTextfield 
                                        label={t("AdminConsole.OrganizationNameLabel")}
                                        onChange={(e) => { setNewOrganizationName(e.target.value) }}
                                        value={newOrganizationName}
                                        needFullWidth
                                        required
                                    />
                                    <KeydriveTextfield 
                                        label={t("AdminConsole.OrganizationDescriptionLabel")}
                                        onChange={(e) => { setNewOrganizationDescription(e.target.value) }}
                                        value={newOrganizationDescription}
                                        needFullWidth
                                    />
                                </Box>
                                { creatingOrganizationerrorMessage && (
                                    <Box marginTop="12px">
                                        <KeyDriveBodyMin 
                                            text={creatingOrganizationerrorMessage}
                                            color="#cf3b3b"
                                            needBold={false}
                                            needEllipses={false}
                                        />
                                    </Box>
                                ) }
                            </>
                        ) }
                    </>,
                    dialogIcon: <Apartment sx={{
                        width: "40px",
                        height: "44px",
                        color: "#22638D"
                    }} />,
                    dialogTitle: t("AdminConsole.OrganizationCreateTitle"),
                    handleTogglDialog: () => {},
                    openDialog: isCreateOrgDialogOpen
                }}
                dialogTitleCenter
                width="560px"
                height="420px"
            />

            <KeyDriveDialog 
                keyDriveDialogButtons={{
                    leftButtonMethod: () => { setConclusionDialog(false) },
                    rightButtonMethod: () => {
                        Edit({
                            allowDrive: organizationInfo.driveAccess,
                            description: organizationInfo.description,
                            name: organizationInfo.name,
                            id: organizationSelected?.id!
                        }).then(() => {
                            setValue(0);
                            getOrganization(organizationSelected?.id!);
                            setConclusionDialog(false);
                        });
                        
                        SetUserService({
                            organizationId: organizationSelected?.id!,
                            serviceUserId: keySelectAdminUsers!
                        })
                        .then(() => {
                            setValue(0);
                            getOrganization(organizationSelected?.id!);
                            setConclusionDialog(false);
                        });
                    },
                    rightButtonText: t("AdminConsole.Continue"),
                    leftButtonText:t("AdminConsole.Cancel")
                }}
                keyDriveDialogContent={{
                    dialogContent: t("AdminConsole.RedirectToHomeOnContinue"),
                    dialogIcon: <Info sx={{ width: "44px", height: "44px", color: "#22638D" }} />,
                    handleTogglDialog: () => {},
                    openDialog: conclusionDialog
                }}
                dialogTitleCenter
            />
        </AdminConsoleWrapper>
    );
};